import { useEffect, useState } from "react";

/** Browser connectivity, from the `online`/`offline` window events (WP-03.1).
 *
 * `navigator.onLine` only knows whether the device has *a* network, not whether this server is reachable, so a
 * screen that must not lose work still has to handle a failed request on its own — the POS till queues the ticket
 * when a checkout throws a connectivity error even while this hook says "online". Use this for telling the person
 * what is going on; use the request's own failure for deciding what to do with their data. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);
  return online;
}

/** A fetch that never reached the server (DNS/connection failure) throws a bare TypeError; a request that did
 * reach it — even a 4xx/5xx — surfaces as the plain Error thrown by lib/frappe.ts's request(). Only the former
 * means "the network is gone"; the latter is a real rejection that would fail identically on retry. */
export function isConnectivityFailure(e: unknown): boolean {
  return e instanceof TypeError;
}
